import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import ImageResize from "tiptap-extension-resize-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { useCallback } from "react";
import {
  HiBold,
  HiItalic,
  HiListBullet,
  HiLink,
  HiPhoto,
  HiNumberedList,
} from "react-icons/hi2";
import {
  RiUnderline,
  RiStrikethrough,
  RiFontColor,
  RiMarkPenLine,
} from "react-icons/ri";
import { LuHeading1, LuHeading2, LuHeading3, LuUndo, LuRedo } from "react-icons/lu";
import ColorChanger from "./ColorChanger";

interface BoardEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
}

export default function BoardEditor({ content, onChange, placeholder = "Enter board content..." }: BoardEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder,
      }),
      // TODO: tiptap-extension-resize-image's types don't expose `allowBase64`
      // even though the underlying Image extension supports it. Pin/upgrade
      // properly later — see option 3 in the deferred plan.
      ImageResize.configure({ allowBase64: true } as Parameters<typeof ImageResize.configure>[0]),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 dark:text-maven-cyan-300 underline",
        },
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      Color,
      TextStyle,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;

        for (const item of items) {
          if (item.type.startsWith("image/")) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) {
              const reader = new FileReader();
              reader.onload = (e) => {
                const base64 = e.target?.result as string;
                view.dispatch(
                  view.state.tr.replaceSelectionWith(
                    view.state.schema.nodes.image.create({ src: base64 })
                  )
                );
              };
              reader.readAsDataURL(file);
            }
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;

        for (const file of files) {
          if (file.type.startsWith("image/")) {
            event.preventDefault();
            const reader = new FileReader();
            reader.onload = (e) => {
              const base64 = e.target?.result as string;
              const { state } = view;
              const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
              if (pos) {
                view.dispatch(
                  state.tr.insert(pos.pos, state.schema.nodes.image.create({ src: base64 }))
                );
              }
            };
            reader.readAsDataURL(file);
            return true;
          }
        }
        return false;
      },
    },
  });

  const addLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL:", previousUrl);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          // TODO: same TipTap typing gap as line 41 — `setImage` is registered
          // by the resize fork at runtime but not surfaced in ChainedCommands.
          (editor.chain().focus() as unknown as { setImage: (opts: { src: string }) => { run: () => void } })
            .setImage({ src: base64 })
            .run();
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border border-gray-300 dark:border-maven-teal-500/40 rounded-lg overflow-hidden bg-white dark:bg-maven-gray-800">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-gray-50 dark:bg-maven-gray-950 border-b border-gray-200 dark:border-maven-teal-500/40">
        {/* Undo/Redo */}
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 text-gray-600 dark:text-maven-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Undo"
        >
          <LuUndo className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 text-gray-600 dark:text-maven-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Redo"
        >
          <LuRedo className="w-4 h-4" />
        </button>

        <div className="w-px h-4 bg-gray-300 dark:bg-maven-gray-800 mx-1" />

        {/* Headings */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 ${editor.isActive("heading", { level: 1 }) ? "bg-maven-teal-500 text-white" : "text-gray-600"}`}
          title="Heading 1"
        >
          <LuHeading1 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 ${editor.isActive("heading", { level: 2 }) ? "bg-maven-teal-500 text-white" : "text-gray-600"}`}
          title="Heading 2"
        >
          <LuHeading2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 ${editor.isActive("heading", { level: 3 }) ? "bg-maven-teal-500 text-white" : "text-gray-600"}`}
          title="Heading 3"
        >
          <LuHeading3 className="w-4 h-4" />
        </button>

        <div className="w-px h-4 bg-gray-300 dark:bg-maven-gray-800 mx-1" />

        {/* Text formatting */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 ${editor.isActive("bold") ? "bg-maven-teal-500 text-white" : "text-gray-600"}`}
          title="Bold"
        >
          <HiBold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 ${editor.isActive("italic") ? "bg-maven-teal-500 text-white" : "text-gray-600"}`}
          title="Italic"
        >
          <HiItalic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 ${editor.isActive("underline") ? "bg-maven-teal-500 text-white" : "text-gray-600"}`}
          title="Underline"
        >
          <RiUnderline className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 ${editor.isActive("strike") ? "bg-maven-teal-500 text-white" : "text-gray-600"}`}
          title="Strikethrough"
        >
          <RiStrikethrough className="w-4 h-4" />
        </button>

        <div className="w-px h-4 bg-gray-300 dark:bg-maven-gray-800 mx-1" />

        {/* Colors */}
        <ColorChanger
          title="Font Color"
          icon={<RiFontColor className="w-4 h-4" />}
          currentColor={editor.getAttributes("textStyle").color}
          handleColorChange={(color) => {
            editor.chain().focus().setColor(color.hex).run();
          }}
          onClearColor={() => {
            editor.chain().focus().unsetColor().run();
          }}
        />
        <ColorChanger
          title="Highlight"
          icon={<RiMarkPenLine className="w-4 h-4" />}
          currentColor={editor.getAttributes("highlight").color}
          handleColorChange={(color) => {
            editor.chain().focus().setHighlight({ color: color.hex }).run();
          }}
          onClearColor={() => {
            editor.chain().focus().unsetHighlight().run();
          }}
        />

        <div className="w-px h-4 bg-gray-300 dark:bg-maven-gray-800 mx-1" />

        {/* Lists */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 ${editor.isActive("bulletList") ? "bg-maven-teal-500 text-white" : "text-gray-600"}`}
          title="Bullet List"
        >
          <HiListBullet className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 ${editor.isActive("orderedList") ? "bg-maven-teal-500 text-white" : "text-gray-600"}`}
          title="Numbered List"
        >
          <HiNumberedList className="w-4 h-4" />
        </button>

        <div className="w-px h-4 bg-gray-300 dark:bg-maven-gray-800 mx-1" />

        {/* Link & Image */}
        <button
          type="button"
          onClick={addLink}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 ${editor.isActive("link") ? "bg-maven-teal-500 text-white" : "text-gray-600"}`}
          title="Add Link"
        >
          <HiLink className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={addImage}
          className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-maven-gray-800 text-gray-600 dark:text-maven-gray-300"
          title="Add Image"
        >
          <HiPhoto className="w-4 h-4" />
        </button>
      </div>

      {/* Editor */}
      <EditorContent
        editor={editor}
        className="board-content p-3 min-h-[200px] max-h-[400px] overflow-y-auto focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[180px] [&_.ProseMirror]:text-gray-900 dark:[&_.ProseMirror]:text-maven-gray-100 [&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mb-2 [&_.ProseMirror_h2]:text-xl [&_.ProseMirror_h2]:font-bold [&_.ProseMirror_h2]:mb-2 [&_.ProseMirror_h3]:text-lg [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:mb-2 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:ml-4 [&_.ProseMirror_ul]:space-y-1 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:ml-4 [&_.ProseMirror_ol]:space-y-1 [&_.ProseMirror_li]:text-gray-900 dark:[&_.ProseMirror_li]:text-maven-gray-100 [&_.ProseMirror_p]:text-gray-900 dark:[&_.ProseMirror_p]:text-maven-gray-100 [&_.ProseMirror_p]:mb-2 [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:h-auto [&_.ProseMirror_img]:rounded [&_.ProseMirror_img]:my-2 [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 dark:[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-maven-gray-500 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.image-resizer]:leading-[0] [&_.image-resizer]:inline-block [&_.image-resizer]:align-bottom [&_img]:block [&_img]:!my-0"
      />
    </div>
  );
}
